/**
 * Ionicon — renders an Ionicons glyph by its exact mobile name
 * (`<Ionicons name="flask-outline" size={20} color={primary} />` on mobile →
 * `<Ionicon name="flask-outline" size={20} color={primary} />` here), inlined
 * as SVG so it inherits `currentColor` when no colour is given.
 *
 * The available names are the ones in ionicon-svgs.ts (every Ionicons name the
 * mobile app references). Add more with `node scripts/gen-ionicons.mjs <name…>`.
 * The older `AppIcon` (legacy base names like 'trash' = trash-outline) stays
 * for existing call sites.
 */

import type { CSSProperties } from 'react'
import { IONICON_SVGS, type IoniconName } from './ionicon-svgs'

export type { IoniconName }

export function isIoniconName(name: string): name is IoniconName {
  return Object.prototype.hasOwnProperty.call(IONICON_SVGS, name)
}

interface IoniconProps {
  name: IoniconName
  /** Glyph box in px. Mobile @expo/vector-icons defaults to 12. */
  size?: number
  /** Any CSS colour (hex, `var(--color-primary)`); defaults to currentColor. */
  color?: string
  className?: string
  style?: CSSProperties
  /** Accessible name; omit for decorative icons (aria-hidden). */
  title?: string
}

export function Ionicon({ name, size = 12, color, className, style, title }: IoniconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 512 512"
      width={size}
      height={size}
      fill="currentColor"
      className={className}
      style={{ flexShrink: 0, ...(color ? { color } : null), ...style }}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
      dangerouslySetInnerHTML={{ __html: IONICON_SVGS[name] }}
    />
  )
}

export default Ionicon
